import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.packUpdate, this.#onPackUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.packUpdate, this.#onPackUpdate);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };

  /**
   * @param {Event} event
   */
  #onPackUpdate = (event) => {
    const { quantity, perItemPriceFormatted, packLabel } = /** @type {any} */ (event).detail;
    const container = this.refs.priceContainer;
    if (!container) return;

    const originalPrice = container.dataset.originalPrice;
    const originalCents = parseInt(container.dataset.originalPriceCents, 10);
    if (!originalPrice || isNaN(originalCents)) return;

    const priceEl = container.querySelector('.price');
    if (!priceEl) return;

    const showSalePriceFirst = container.dataset.showSalePriceFirst !== 'false';

    const packCents = this.#parsePriceToCents(perItemPriceFormatted);
    const hasRealDiscount = quantity > 1 && perItemPriceFormatted && packCents !== null && packCents < originalCents;

    // Remove any previously injected pack pricing elements
    for (const el of container.querySelectorAll('.pack-price-original, .pack-price-detail')) {
      el.remove();
    }

    if (hasRealDiscount) {
      priceEl.textContent = perItemPriceFormatted;

      const detailEl = document.createElement('span');
      detailEl.className = 'pack-price-detail';
      const packContext = packLabel ? ` (${packLabel})` : '';
      detailEl.textContent = `/ Tee${packContext}`;

      const strikethroughEl = document.createElement('span');
      strikethroughEl.className = 'pack-price-original compare-at-price';
      strikethroughEl.setAttribute('aria-label', `Original price: ${originalPrice}`);
      strikethroughEl.textContent = originalPrice;

      if (showSalePriceFirst) {
        priceEl.after(detailEl, strikethroughEl);
      } else {
        priceEl.before(strikethroughEl);
        priceEl.after(detailEl);
      }
    } else {
      priceEl.textContent = originalPrice;
    }
  };

  /**
   * Extracts a cents value from a formatted price string like "$17", "$17.00", "17"
   * @param {string} priceStr
   * @returns {number|null}
   */
  #parsePriceToCents(priceStr) {
    if (!priceStr) return null;
    const cleaned = priceStr.replace(/[^0-9.,]/g, '');
    if (!cleaned) return null;

    const parts = cleaned.split(/[.,]/);
    if (parts.length === 0) return null;

    const lastPart = parts[parts.length - 1];
    const hasDecimals = parts.length > 1 && lastPart.length <= 2;

    if (hasDecimals) {
      const whole = parseInt(parts.slice(0, -1).join(''), 10) || 0;
      const fraction = parseInt(lastPart.padEnd(2, '0'), 10) || 0;
      return whole * 100 + fraction;
    }

    const whole = parseInt(parts.join(''), 10);
    return isNaN(whole) ? null : whole * 100;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
